import { MutationFunction } from "@apollo/client";
import downloadIcon from "@assets/images/download-teal.svg";
import plusIcon from "@assets/images/plus.svg";
import { Button } from "@material-ui/core";
import { addAlternatedMaterialUrl } from "@saleor/alternatedMaterial/urls";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import {
  ExportAlternativeMaterialMutation,
  ExportAlternativeMaterialMutationVariables
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { formatDateTime } from "@saleor/utils/date";
import React from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import MyTypography from "../../../components/MyTypography";
import useAppState from "../../../hooks/useAppState";

interface propsStyleType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsStyleType>(
  theme => ({
    bottomBar: {
      position: "fixed",
      bottom: 0,
      background: "#FFF",
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: "16px 24px",
      height: "72px",
      width: props =>
        props.expandMenu
          ? `calc(100% - ${menuWidth}px - 50px - ${props.bodyPaddingRight}px)`
          : `calc(100% - ${shrunkMenuWidth}px - 50px - ${props.bodyPaddingRight}px)`,
      display: "flex",
      alignItems: "center",
      zIndex: 9,
      boxShadow: "0px -8px 24px rgba(0, 0, 0, 0.08)"
    },
    barRight: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center"
    },
    btnOrder: {
      marginLeft: 20,
      maxHeight: "40px"
    },
    btnDownload: {
      color: theme.palette.info.main,
      fontSize: "16px",
      fontWeight: 700
    },
    btnText: {
      color: "white",
      fontSize: "16px",
      fontWeight: 400
    },
    btnBottomBar: {
      maxHeight: "40px"
    },
    updateTime: {
      color: theme.palette.info.main,
      fontSize: "14px",
      marginRight: "8px"
    },
    updateDate: {
      fontSize: "14px"
    }
  }),
  { name: "BottomBar" }
);

interface BottomBarProps {
  onDownloadFile: MutationFunction<
    ExportAlternativeMaterialMutation,
    ExportAlternativeMaterialMutationVariables
  >;
  lastUpdateDate: string;
}

const BottomBar: React.FC<BottomBarProps> = ({
  onDownloadFile,
  lastUpdateDate
}) => {
  const [appState] = useAppState();
  const { expandMenu, bodyPaddingRight } = appState;
  const classes = useStyles({ expandMenu, bodyPaddingRight });
  const navigate = useNavigator();

  const handleDownloadFile = async () => {
    const { data } = await onDownloadFile();
    const link = document.getElementById("download") as HTMLAnchorElement;
    link.href = `data:${data.exportAlternativeMaterial?.contentType};base64,${data.exportAlternativeMaterial?.exportedFileBase64}`;
    link.target = "_self";
    link.download = data.exportAlternativeMaterial?.fileName;
    link.click();
  };

  return (
    <div className={classes.bottomBar}>
      <div>
        <MyTypography variant="caption">
          <span className={classes.updateTime}>
            <FormattedMessage defaultMessage="Last updated date " />
          </span>
          <span className={classes.updateDate}>
            {" "}
            {formatDateTime(lastUpdateDate)}
          </span>
        </MyTypography>
      </div>
      <div className={classes.barRight}>
        <div className={classes.btnOrder}>
          <Button
            id="downloadFile"
            variant="outlined"
            color="secondary"
            onClick={handleDownloadFile}
            className={classes.btnBottomBar}
            data-testid="downloadFile"
          >
            <SVG src={downloadIcon} />
            <MyTypography variant="body1" className={classes.btnDownload}>
              <FormattedMessage defaultMessage={"Download File"} />
            </MyTypography>
          </Button>
          <a id="download" download>
            &nbsp;
          </a>
        </div>
        <div className={classes.btnOrder}>
          <Button
            id="addMasterData"
            onClick={() => navigate(addAlternatedMaterialUrl)}
            variant={"contained"}
            color={"secondary"}
            className={classes.btnBottomBar}
            data-testid="addMasterData"
          >
            <SVG src={plusIcon} />
            <MyTypography variant="subtitle1" className={classes.btnText}>
              <b> Add Master</b> - Alternated Material
            </MyTypography>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BottomBar;
